import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAButton from '../shared/SPAButton';
import SPADataTable from '../shared/SPADataTable';
import SPAPill from '../shared/SPAPill';
import SPASectionHeading from '../shared/SPASectionHeading';
import { Consumer } from './types';
import { formatTokens } from './usagePalette';

const columnHelper = createColumnHelper<Consumer>();

interface TopConsumersProps {
  consumers?: Consumer[];
  days: number;
  onSelect: (identity: string) => void;
}

// The row button only fills the limit form — the ellipsis marks that, since the form's own button
// is what writes to the gateway.
const TopConsumers = ({ consumers, days, onSelect }: TopConsumersProps) => {
  const columns = [
    columnHelper.accessor('name', {
      cell: info => (
        <span className="tw-flex tw-items-center tw-gap-2">
          {info.getValue()}
          {info.row.original.throttled && <SPAPill tone="warning">Limited</SPAPill>}
        </span>
      ),
      header: 'User',
    }),
    columnHelper.accessor('identity', {
      cell: info => <span className="tw-font-mono tw-text-xs">{info.getValue()}</span>,
      header: 'Identity',
    }),
    columnHelper.accessor('requests', {
      cell: info => info.getValue().toLocaleString(),
      header: 'Requests',
      meta: { align: 'right' },
    }),
    columnHelper.accessor('tokens', {
      cell: info => formatTokens(info.getValue()),
      header: 'Tokens',
      meta: { align: 'right' },
    }),
    columnHelper.display({
      cell: info => (
        <SPAButton size="xs" variant="secondary" onClick={() => onSelect(info.row.original.identity)}>
          Set limit&hellip;
        </SPAButton>
      ),
      header: () => <span className="tw-sr-only">Actions</span>,
      id: 'actions',
      meta: { align: 'right', width: 1 },
    }),
  ] as ColumnDef<Consumer, unknown>[];

  return (
    <section>
      <SPASectionHeading meta={`By tokens · last ${days} days`} title="Top consumers" />
      <SPADataTable
        columns={columns}
        data={consumers ?? []}
        defaultSort={[]}
        emptyMessage="No AI calls recorded in this window."
        isLoading={!consumers}
        loadingRows={5}
      />
    </section>
  );
};

export default TopConsumers;
